declare global {
  interface Window {
    dataLayer: any[];
  }
}

export type GTMEvent =
  | "quiz_started"
  | "quiz_step_completed"
  | "quiz_dog_added"
  | "quiz_completed"
  | "results_viewed"
  | "results_step_viewed"
  | "plan_selected"
  | "plan_changed"
  | "meal_mode_changed"
  | "meal_selected"
  | "meal_selection_completed"
  | "treat_selected"
  | "promo_code_applied"
  | "checkout_initiated"
  | "checkout_button_clicked"
  | "experiment_assignment"
  | "experiment_exposure";

interface DogData {
  dogId: string;
  name?: string;
  breed?: string;
  ageYears?: number;
  ageMonths?: number;
  weightKg?: number;
  activityLevel?: string;
  bodyCondition?: string;
  neutered?: boolean;
  allergies?: string[];
  healthIssues?: string[];
  dailyGrams?: number;
  dailyCalories?: number;
}

interface PlanData {
  planDays: 7 | 14 | 28;
  packSize?: number;
  totalPacks?: number;
  pricePerDay?: number;
  totalPrice?: number;
  mealMode?: "FULL" | "MIXED";
}

interface MealData {
  variantId: string;
  recipeName: string;
  quantity: number;
  price?: number;
}

interface CheckoutData {
  totalValue: number;
  currency: string;
  numberOfDogs: number;
  subscriptionFrequency?: number;
  promoCode?: string;
  discountAmount?: number;
}

function ensureDataLayer() {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
}

// Active experiment context as "key:variant,key2:variant2" from the store
// exposed by snippets/paws-experiments.liquid. QA overrides are excluded so
// dashboards never mix QA traffic into experiment segments.
function activeExperiments(): string {
  if (typeof window === "undefined") return "";
  const store = window.__PAWS_EXPERIMENTS__;
  if (!store || !store.assignments) return "";
  const parts: string[] = [];
  for (const [key, rec] of Object.entries(store.assignments)) {
    if (rec && rec.variant && rec.src !== "qa_override") {
      parts.push(`${key}:${rec.variant}`);
    }
  }
  return parts.join(",");
}

function pushToDataLayer(data: Record<string, any>) {
  ensureDataLayer();
  const experiments = activeExperiments();
  const enrichedData = {
    ...data,
    timestamp: new Date().toISOString(),
    page_path: window.location.pathname,
    page_url: window.location.href,
    // Every funnel event inherits experiment context for GA4 segmentation.
    ...(experiments && { experiments }),
  };
  window.dataLayer.push(enrichedData);
}

export function trackQuizStarted() {
  pushToDataLayer({
    event: "quiz_started",
    event_category: "Quiz",
    event_label: "Quiz Started",
  });
}

export function trackQuizStepCompleted(params: {
  stepId: string;
  stepNumber: number;
  dogId: string;
  dogName?: string;
  answers?: Record<string, string | number | boolean | undefined>;
}) {
  // Flatten the step's answers into the event so GTM/GA4 can segment the
  // funnel by what users actually told us (breed, weight, activity, ...).
  // Owner contact details are deliberately excluded — no PII into GA4.
  const answers: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(params.answers ?? {})) {
    if (value !== undefined && value !== "") answers[key] = value;
  }

  pushToDataLayer({
    event: "quiz_step_completed",
    event_category: "Quiz",
    event_label: `Step ${params.stepNumber} - ${params.stepId}`,
    step_id: params.stepId,
    step_number: params.stepNumber,
    dog_id: params.dogId,
    dog_name: params.dogName || "Unknown",
    ...answers,
  });
}

export function trackDogAdded(params: {
  dogId: string;
  dogNumber: number;
  dogName?: string;
}) {
  pushToDataLayer({
    event: "quiz_dog_added",
    event_category: "Quiz",
    event_label: `Dog ${params.dogNumber} Added`,
    dog_id: params.dogId,
    dog_number: params.dogNumber,
    dog_name: params.dogName || "Unknown",
  });
}

export function trackQuizCompleted(params: {
  numberOfDogs: number;
  totalDailyGrams: number;
  dogs: Partial<DogData>[];
  ownerName?: string;
  ownerEmail?: string;
}) {
  // Meta browser Lead event (fires only when the theme's Meta pixel is
  // enabled). Browser-only by design — no server copy, so no dedup needed.
  const fbq = (window as any).fbq;
  if (typeof fbq === "function") {
    fbq("track", "Lead", { content_name: "quiz_completed" });
  }

  pushToDataLayer({
    event: "quiz_completed",
    event_category: "Quiz",
    event_label: "Quiz Completed",
    number_of_dogs: params.numberOfDogs,
    total_daily_grams: params.totalDailyGrams,
    owner_name: params.ownerName,
    owner_email: params.ownerEmail,
    dogs: params.dogs.map((dog) => ({
      dog_id: dog.dogId,
      dog_name: dog.name,
      breed: dog.breed,
      age_years: dog.ageYears,
      weight_kg: dog.weightKg,
      daily_grams: dog.dailyGrams,
      daily_calories: dog.dailyCalories,
    })),
  });
}

export function trackResultsViewed(params: {
  numberOfDogs: number;
  totalDailyGrams: number;
  ownerName?: string;
  ownerEmail?: string;
}) {
  pushToDataLayer({
    event: "results_viewed",
    event_category: "Results",
    event_label: "Results Page Viewed",
    number_of_dogs: params.numberOfDogs,
    total_daily_grams: params.totalDailyGrams,
    owner_name: params.ownerName,
    owner_email: params.ownerEmail,
  });
}

export function trackResultsStepViewed(params: { stepNumber: number; stepName: string }) {
  pushToDataLayer({
    event: "results_step_viewed",
    event_category: "Results",
    event_label: `Step ${params.stepNumber} - ${params.stepName}`,
    results_step: params.stepNumber,
    step_name: params.stepName,
  });
}

export function trackPlanSelected(params: PlanData & { isInitialSelection: boolean }) {
  pushToDataLayer({
    event: params.isInitialSelection ? "plan_selected" : "plan_changed",
    event_category: "Results",
    event_label: `${params.planDays}-Day Plan`,
    plan_days: params.planDays,
    pack_size: params.packSize,
    total_packs: params.totalPacks,
    price_per_day: params.pricePerDay,
    total_price: params.totalPrice,
    meal_mode: params.mealMode,
    value: params.totalPrice,
    currency: "GBP",
  });
}

export function trackMealModeChanged(params: {
  previousMode: "FULL" | "MIXED";
  newMode: "FULL" | "MIXED";
  planDays: 7 | 14 | 28;
}) {
  pushToDataLayer({
    event: "meal_mode_changed",
    event_category: "Results",
    event_label: `Changed to ${params.newMode === "FULL" ? "100%" : "60-40"}`,
    previous_mode: params.previousMode,
    new_mode: params.newMode,
    plan_days: params.planDays,
  });
}

export function trackMealSelected(params: {
  meal: MealData;
  dogId: string;
  dogName?: string;
  totalSelected: number;
  recommended: number;
}) {
  pushToDataLayer({
    event: "meal_selected",
    event_category: "Results",
    event_label: params.meal.recipeName,
    dog_id: params.dogId,
    dog_name: params.dogName,
    recipe_name: params.meal.recipeName,
    variant_id: params.meal.variantId,
    quantity: params.meal.quantity,
    price: params.meal.price,
    total_selected: params.totalSelected,
    recommended: params.recommended,
    selection_complete: params.totalSelected >= params.recommended,
  });
}

export function trackMealSelectionCompleted(params: {
  dogId: string;
  dogName?: string;
  meals: MealData[];
  totalPacks: number;
  recommendedPacks: number;
}) {
  const totalValue = params.meals.reduce(
    (sum, meal) => sum + (meal.price || 0) * meal.quantity,
    0
  );

  pushToDataLayer({
    event: "meal_selection_completed",
    event_category: "Results",
    event_label: `${params.dogName || "Dog"} Meals Complete`,
    dog_id: params.dogId,
    dog_name: params.dogName,
    total_packs: params.totalPacks,
    recommended_packs: params.recommendedPacks,
    selected_recipes: params.meals.map((m) => m.recipeName).join(", "),
    value: totalValue,
    currency: "GBP",
  });
}

export function trackTreatSelected(params: {
  variantId: string;
  treatName: string;
  quantity: number;
  price?: number;
}) {
  pushToDataLayer({
    event: "treat_selected",
    event_category: "Results",
    event_label: params.treatName,
    treat_name: params.treatName,
    variant_id: params.variantId,
    quantity: params.quantity,
    price: params.price,
    value: (params.price || 0) * params.quantity,
    currency: "GBP",
  });
}

export function trackPromoCodeApplied(params: {
  promoCode: string;
  discountType?: string;
  discountAmount?: number;
}) {
  pushToDataLayer({
    event: "promo_code_applied",
    event_category: "Results",
    event_label: params.promoCode,
    promo_code: params.promoCode,
    discount_type: params.discountType,
    discount_amount: params.discountAmount,
  });
}

export function trackCheckoutInitiated(
  params: CheckoutData & {
    ownerName?: string;
    ownerEmail?: string;
  }
) {
  pushToDataLayer({
    event: "checkout_initiated",
    event_category: "Checkout",
    event_label: "Checkout Started",
    value: params.totalValue,
    currency: params.currency || "GBP",
    number_of_dogs: params.numberOfDogs,
    subscription_frequency: params.subscriptionFrequency,
    promo_code: params.promoCode,
    discount_amount: params.discountAmount,
    owner_name: params.ownerName,
    owner_email: params.ownerEmail,
    ecommerce: {
      checkout: {
        actionField: { step: 1, option: "Quiz Flow" },
        value: params.totalValue,
      },
    },
  });
}

export function trackCheckoutButtonClicked(params: {
  buttonLocation: string;
  totalValue: number;
}) {
  pushToDataLayer({
    event: "checkout_button_clicked",
    event_category: "Checkout",
    event_label: params.buttonLocation,
    button_location: params.buttonLocation,
    value: params.totalValue,
    currency: "GBP",
  });
}

export function trackExperimentExposure(params: {
  experimentKey: string;
  version: number;
  variant: string;
  source: string;
}) {
  pushToDataLayer({
    event: "experiment_exposure",
    event_category: "Experiments",
    event_label: `${params.experimentKey}:${params.variant}`,
    experiment_key: params.experimentKey,
    experiment_version: params.version,
    variant: params.variant,
    assignment_source: params.source,
  });
}

export function trackPageView(params: { pageTitle: string; pagePath: string }) {
  pushToDataLayer({
    event: "virtual_pageview",
    page_title: params.pageTitle,
    page_path: params.pagePath,
    page_location: window.location.href,
  });
}

export const GTM = {
  trackQuizStarted,
  trackQuizStepCompleted,
  trackDogAdded,
  trackQuizCompleted,
  trackResultsViewed,
  trackResultsStepViewed,
  trackPlanSelected,
  trackMealModeChanged,
  trackMealSelected,
  trackMealSelectionCompleted,
  trackTreatSelected,
  trackPromoCodeApplied,
  trackCheckoutInitiated,
  trackCheckoutButtonClicked,
  trackExperimentExposure,
  trackPageView,
};
